import { useEffect, useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { Loader2, Lock, Mail, MessageCircle } from "lucide-react";

import { AdminTopbar } from "@/components/admin/AdminTopbar";
import { MercadoPagoSettings } from "@/components/admin/config/MercadoPagoSettings";
import { AdminCard, ListState } from "@/components/admin/primitives";
import { Switch } from "@/components/ui/switch";
import { formatDateTime } from "@/lib/dates";
import { errorMessage } from "@/lib/errors";
import { formatStorePhone, useAdminSettings, useUpdateSettings } from "@/lib/settings";

/**
 * Configurações da loja: o que muda sem deploy.
 *
 * Só administração. Cada cartão salva sozinho — trocar o número do WhatsApp
 * não pode, por engano, ligar o repasse junto.
 */
export const Route = createFileRoute("/admin/_painel/configuracoes")({
  head: () => ({ meta: [{ title: "Configurações — Painel MonteBox" }] }),
  component: AdminSettings,
});

const INPUT =
  "mt-1.5 h-11 w-full rounded-mb-field border border-mb-line bg-white px-4 text-sm text-mb-ink outline-none transition-colors focus:border-mb-navy";

function AdminSettings() {
  const settings = useAdminSettings();
  const update = useUpdateSettings();
  const [phone, setPhone] = useState("");
  const [notifyEmail, setNotifyEmail] = useState("");

  useEffect(() => {
    if (!settings.data) return;
    setPhone(settings.data.whatsappNumber ?? "");
    setNotifyEmail(settings.data.notifyEmail ?? "");
  }, [settings.data]);

  if (!settings.data) {
    return (
      <>
        <AdminTopbar title="Configurações" />
        <AdminCard className="m-8">
          <ListState
            loading={settings.isLoading}
            error={settings.error}
            empty={false}
            emptyLabel=""
          />
        </AdminCard>
      </>
    );
  }

  const data = settings.data;
  const digits = phone.replace(/\D/g, "");
  const phoneChanged = digits !== (data.whatsappNumber ?? "");

  const savePhone = (event: FormEvent) => {
    event.preventDefault();
    update.mutate(
      { whatsappNumber: digits || null },
      {
        onSuccess: () => toast.success("WhatsApp da loja salvo."),
        onError: (cause) => toast.error(errorMessage(cause)),
      },
    );
  };

  const emailChanged = notifyEmail.trim().toLowerCase() !== (data.notifyEmail ?? "");

  const saveEmail = (event: FormEvent) => {
    event.preventDefault();
    update.mutate(
      { notifyEmail: notifyEmail.trim() || null },
      {
        onSuccess: () => toast.success("E-mail da equipe salvo."),
        onError: (cause) => toast.error(errorMessage(cause)),
      },
    );
  };

  const toggleLock = (locked: boolean) => {
    if (
      locked &&
      !window.confirm(
        "Travar os repasses?\n\nNenhum repasse aos parceiros sai até destravar. As comissões continuam sendo geradas e liberadas normalmente.",
      )
    ) {
      return;
    }
    update.mutate(
      { payoutsLocked: locked },
      {
        onSuccess: () => toast.success(locked ? "Repasses travados." : "Repasses destravados."),
        onError: (cause) => toast.error(errorMessage(cause)),
      },
    );
  };

  return (
    <>
      <AdminTopbar title="Configurações" />
      <div className="flex max-w-3xl flex-col gap-6 p-4 sm:p-8">
        <AdminCard className="p-6">
          <h2 className="flex items-center gap-2 font-display text-base font-bold text-mb-navy">
            <MessageCircle className="h-[18px] w-[18px]" />
            WhatsApp da loja
          </h2>
          <p className="mt-1 text-xs leading-4 text-mb-muted text-pretty">
            Recebe o aviso "Pedido #xxxx efetuado no site" que o cliente pode mandar depois de
            enviar, e as conversas que ele abre pelo painel dele. Sem número, o WhatsApp abre
            pedindo para o cliente escolher o contato.
          </p>
          <form onSubmit={savePhone} className="mt-4 flex items-end gap-3">
            <label className="block grow text-xs font-bold text-mb-ink">
              Número com DDI e DDD
              <input
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                inputMode="tel"
                placeholder="+55 11 99999-9999"
                className={INPUT}
              />
            </label>
            <button
              type="submit"
              disabled={update.isPending || !phoneChanged}
              className="flex h-11 items-center gap-2 rounded-mb-button bg-mb-navy px-5 text-sm font-bold text-white disabled:opacity-50"
            >
              {update.isPending && update.variables?.whatsappNumber !== undefined && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Salvar
            </button>
          </form>
          <p className="mt-2 text-[11px] text-mb-muted">
            {data.whatsappNumber
              ? `Hoje: ${formatStorePhone(data.whatsappNumber)}`
              : "Nenhum número cadastrado."}
          </p>
        </AdminCard>

        <AdminCard className="p-6">
          <h2 className="flex items-center gap-2 font-display text-base font-bold text-mb-navy">
            <Mail className="h-[18px] w-[18px]" />
            E-mail da equipe
          </h2>
          <p className="mt-1 text-xs leading-4 text-mb-muted text-pretty">
            Recebe "Pedido #xxxx efetuado no site" toda vez que um cliente envia um pedido, com o
            resumo e o link para abrir no painel. Vazio, o pedido novo aparece só no painel.
          </p>
          <form onSubmit={saveEmail} className="mt-4 flex items-end gap-3">
            <label className="block grow text-xs font-bold text-mb-ink">
              E-mail
              <input
                type="email"
                value={notifyEmail}
                onChange={(event) => setNotifyEmail(event.target.value)}
                placeholder="pedidos@suaempresa.com.br"
                className={INPUT}
              />
            </label>
            <button
              type="submit"
              disabled={update.isPending || !emailChanged}
              className="flex h-11 items-center gap-2 rounded-mb-button bg-mb-navy px-5 text-sm font-bold text-white disabled:opacity-50"
            >
              Salvar
            </button>
          </form>
        </AdminCard>

        <AdminCard className="p-6">
          <div className="flex items-start justify-between gap-6">
            <div>
              <h2 className="flex flex-wrap items-center gap-2 font-display text-base font-bold text-mb-navy">
                <Lock className="h-[18px] w-[18px]" />
                Travar repasses
              </h2>
              <p className="mt-1 text-xs leading-4 text-mb-muted text-pretty">
                Os repasses aos parceiros são automáticos pelo Mercado Pago: cada comissão liberada
                vai para a conta que o parceiro conectou, no prazo de recebimento da conta da loja
                mais os dias adicionais (em Mercado Pago, abaixo). Trave para segurar todos os
                repasses — numa auditoria, numa disputa ou num problema na conta. As comissões
                continuam sendo geradas e liberadas, e saem quando destravar.
              </p>
            </div>
            <Switch
              checked={data.payoutsLocked}
              disabled={update.isPending}
              onCheckedChange={toggleLock}
              aria-label="Travar repasses"
            />
          </div>
          <p
            className={
              data.payoutsLocked
                ? "mt-3 text-xs font-bold text-amber-800"
                : "mt-3 text-xs font-bold text-emerald-700"
            }
          >
            {data.payoutsLocked ? "Travado — nenhum repasse sai" : "Destravado"}
          </p>
        </AdminCard>

        <MercadoPagoSettings config={data.mercadoPago} payoutsLocked={data.payoutsLocked} />

        <p className="text-[11px] text-mb-muted">
          Última alteração em {formatDateTime(data.updatedAt)}.
        </p>
      </div>
    </>
  );
}
